// card-sandbox: build the srcdoc document for an opaque-origin sandboxed card (A1).
//
// THIS IS THE ORIGIN-MODEL SEAM (see exploration/card-sandbox.md § Migration). The
// A1 (opaque, no-infra) model inlines the card JS into a srcdoc string the iframe
// runs. Migrating to distinct origins (B, *.cards.aveq.io) replaces ONLY this
// function with `iframe.src = https://<app>.cards.aveq.io/...` + a serving vhost;
// the protocol, action gate, storage API, and source guard are unchanged.
//
// The document is loaded into `<iframe sandbox="allow-scripts">` with NO
// `allow-same-origin`, so its origin is `null`: no cookies, no parent DOM, no
// parent localStorage. The inline CSP additionally denies ALL network — a card can
// only talk to the host via postMessage. Card code breaking out of the `<script>`
// tag would still run inside this same sandbox (no escalation), but we escape
// `</script>` anyway so a card can't corrupt the document/bootstrap structure.

import { PROTOCOL_VERSION } from './protocol';

// The SDK the card authors against, injected ahead of the card JS. Runs INSIDE the
// sandboxed iframe. It handshakes, bridges actions/resize/storage over postMessage,
// and auto-reports height. A card uses `window.shastaCard`:
//   shastaCard.onInit((structured, ctx) => { render into #shasta-root })
//   shastaCard.emitAction(action_spec, structured?)
//   await shastaCard.storage.get(key) / shastaCard.storage.set(key, value)
export const CARD_SDK_BOOTSTRAP = `(function () {
  var PROTO = ${PROTOCOL_VERSION};
  var initCb = null, pushCb = null, lifecycleCb = null, lastLifecycle = null, pending = {}, seq = 0;
  function post(m) { window.parent.postMessage(m, '*'); }
  function req(op, key, value) {
    return new Promise(function (resolve) {
      var reqId = 'r' + (++seq);
      pending[reqId] = resolve;
      post({ type: 'card:storage', reqId: reqId, op: op, key: key, value: value });
    });
  }
  var idleMs = 0, idleTimer = null, idleHidden = false;
  function armIdle() {
    if (idleTimer) clearTimeout(idleTimer);
    idleTimer = null;
    if (!idleMs || idleHidden) return;
    idleTimer = setTimeout(function () { idleMs = 0; post({ type: 'card:end' }); }, idleMs);
  }
  ['pointerdown', 'keydown', 'wheel', 'touchstart'].forEach(function (t) {
    document.addEventListener(t, armIdle, { capture: true, passive: true });
  });
  window.shastaCard = {
    onInit: function (cb) { initCb = cb; },
    emitAction: function (action, structured) {
      post({ type: 'card:action', action: action, structured: structured });
    },
    // v2 messaging channel (ADR 2026-07-24-card-sandbox-messaging). invoke() is a
    // request/response call to the card's OWN helper — the host gates + stamps
    // identity, dispatches over the chat session, and resolves this Promise with
    // { ok, value }. onPush() receives server-originated, session-scoped messages.
    // The card still has NO network; the host is the transport.
    invoke: function (action, structured) {
      return new Promise(function (resolve) {
        var reqId = 'i' + (++seq);
        pending[reqId] = resolve;
        post({ type: 'card:invoke', reqId: reqId, action: action, structured: structured });
      });
    },
    onPush: function (cb) { pushCb = cb; },
    // Lifecycle: host signals 'suspend' (chat WebSocket down) / 'resume' (back up).
    // Buffers the last event so a card that registers late still sees current state.
    onLifecycle: function (cb) { lifecycleCb = cb; if (lastLifecycle) cb(lastLifecycle); },
    resize: function (height) { post({ type: 'card:resize', height: height }); },
    // Ask the host to bring this card into view. A card cannot scroll the page — it has no
    // parent DOM — so it can only say that something is happening in here. The host decides,
    // and does nothing if the card is already visible.
    reveal: function () { post({ type: 'card:reveal' }); },
    // A live card that has finished: its chat link shows "ended".
    end: function () { post({ type: 'card:end' }); },
    // End once the card has been on screen this long with no input and no push.
    endWhenIdle: function (ms) { idleMs = ms > 0 ? ms : 0; armIdle(); },
    storage: {
      get: function (key) { return req('get', key); },
      set: function (key, value) { return req('set', key, value); }
    }
  };
  // Auto-resize: report the card's rendered height so the host sizes the iframe.
  // Robust by design — a plain ResizeObserver alone proved unreliable here (the
  // Climate Lab port rendered 627px but the observer never delivered a non-zero
  // height; documentElement's box is the fixed iframe viewport, and body's box
  // change didn't fire on React's async paint). So: measure on a brief poll right
  // after init (catches the async first paint) AND keep an observer for later
  // dynamic changes (slider interactions that grow/shrink the card).
  var lastH = 0;
  function measure() {
    var h = Math.max(
      document.body ? document.body.scrollHeight : 0,
      document.documentElement ? document.documentElement.scrollHeight : 0
    );
    if (h && h !== lastH) { lastH = h; post({ type: 'card:resize', height: h }); }
  }
  function startAutoResize() {
    measure();
    var n = 0;
    var iv = setInterval(function () { measure(); if (++n > 13) clearInterval(iv); }, 150); // ~2s
    if (window.ResizeObserver) {
      new ResizeObserver(measure).observe(document.body || document.documentElement);
    }
  }
  // card-pager-swipe: a horizontal touch swipe that an inner horizontal scroller
  // (e.g. a station timeline) does NOT consume becomes a page hint the host pager
  // may act on. The card has no idea it's in a pager — it just reports the gesture;
  // a non-paged card's host simply ignores it.
  // KEEP the yield/threshold logic in sync with the host-side copy for INLINE cards in
  // aveq-app/src/viewers/CardViewer.js (hScrollerFrom + onTouchEnd) — same decision both sides.
  var sX = 0, sY = 0, sTrack = false, sScroller = null;
  function hScroller(el) {
    for (; el && el !== document.body && el !== document.documentElement; el = el.parentNode) {
      if (el.scrollWidth - el.clientWidth > 2) {
        var ox = window.getComputedStyle ? getComputedStyle(el).overflowX : '';
        if (ox === 'auto' || ox === 'scroll') return el;
      }
    }
    return null;
  }
  document.addEventListener('touchstart', function (e) {
    if (!e.touches || e.touches.length !== 1) { sTrack = false; return; }
    sX = e.touches[0].clientX; sY = e.touches[0].clientY; sTrack = true;
    sScroller = hScroller(e.target);
  }, { passive: true });
  document.addEventListener('touchend', function (e) {
    if (!sTrack) return;
    sTrack = false;
    var t = e.changedTouches && e.changedTouches[0];
    if (!t) return;
    var dx = t.clientX - sX, dy = t.clientY - sY;
    // A deliberate, mostly-horizontal swipe past the threshold — else it's a tap or a scroll.
    if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy) * 1.4) return;
    // If an inner horizontal scroller can still move the way we swiped, it owns the gesture.
    if (sScroller) {
      var maxL = sScroller.scrollWidth - sScroller.clientWidth;
      if (dx > 0 && sScroller.scrollLeft > 1) return;          // swipe right, but can still scroll left
      if (dx < 0 && sScroller.scrollLeft < maxL - 1) return;   // swipe left, but can still scroll right
    }
    post({ type: 'card:swipe', dir: dx < 0 ? 'next' : 'prev' });
  }, { passive: true });
  window.addEventListener('message', function (e) {
    if (e.source !== window.parent) return;
    var m = e.data || {};
    if (m.type === 'host:init') {
      // resync=true (v2) tells a stateful card to REQUEST current state via
      // invoke() rather than expect a replayed payload (refresh = resync).
      var rm = !!m.reduceMotion;
      try { rm = rm || window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (x) {}
      if (initCb) initCb(m.structured, { theme: m.theme, kind: m.kind, resync: !!m.resync,
                                         reduceMotion: rm });
      startAutoResize();
    } else if (m.type === 'host:storage:result') {
      var r = pending[m.reqId];
      if (r) { delete pending[m.reqId]; r(m.ok ? m.value : undefined); }
    } else if (m.type === 'host:invoke:result') {
      var ri = pending[m.reqId];
      if (ri) { delete pending[m.reqId]; ri({ ok: !!m.ok, value: m.value }); }
    } else if (m.type === 'host:push') {
      armIdle();
      if (pushCb) pushCb(m.message, m.channel);
    } else if (m.type === 'host:lifecycle') {
      if (m.event === 'hide' || m.event === 'show') { idleHidden = m.event === 'hide'; armIdle(); }
      lastLifecycle = m.event;
      if (lifecycleCb) lifecycleCb(m.event);
    }
  });
  post({ type: 'card:ready', protocol: PROTO });
})();`;

// Strict CSP: no network of any kind (default-src 'none'); inline script + style
// only (the bootstrap + card are inlined; cards style via inline). No img/connect/
// frame — a v1 card renders from its payload and talks to the host by postMessage
// alone. (When a card legitimately needs an image, that becomes a host-mediated
// asset, not a card network reach — a later decision.)
const CSP = "default-src 'none'; script-src 'unsafe-inline'; style-src 'unsafe-inline'; base-uri 'none'; form-action 'none'";

function escapeScript(js) {
  // Keep the card from terminating the <script> or opening a comment/CDATA that
  // corrupts the document. No security escalation (all inside the sandbox), purely
  // structural integrity of the srcdoc.
  return String(js).replace(/<\/(script)/gi, '<\\/$1').replace(/<!--/g, '<\\!--');
}

export function buildCardDocument(cardJs) {
  return `<!doctype html><html><head><meta charset="utf-8">`
    + `<meta http-equiv="Content-Security-Policy" content="${CSP}">`
    + `<style>html,body{margin:0;padding:0;font-family:sans-serif}</style></head>`
    + `<body><div id="shasta-root"></div>`
    + `<script>${CARD_SDK_BOOTSTRAP}</script>`
    + `<script>${escapeScript(cardJs)}</script>`
    + `</body></html>`;
}
